import React, { useEffect, useState } from 'react'
import { Drawer, Table, TablePaginationConfig, Typography, Tooltip } from 'antd'
import type { ColumnType } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import styled from 'styled-components'
import { useGetTaskDetail, useGetTaskDetailTemplateList } from './utils'
import { SignTitle } from '@/components'
import moment from 'moment'
import CaseModal from './case-modal'
import { t } from '@/i18n'

interface PropsType {
  open: boolean
  onClose: () => void
  id: string
}

export enum Baswafv1Encode {
  EncodeDynamic = 'EncodeDynamic',
  EncodeStatic = 'EncodeStatic',
  EncodeUnknown = 'EncodeUnknown',
}

export enum Baswafv1TaskKindDetail {
  TaskKindDetailBooking = 'TaskKindDetailBooking',
  TaskKindDetailCycleDay = 'TaskKindDetailCycleDay',
  TaskKindDetailCycleMonth = 'TaskKindDetailCycleMonth',
  TaskKindDetailCycleWeek = 'TaskKindDetailCycleWeek',
  TaskKindDetailImmediately = 'TaskKindDetailImmediately',
  TaskKindDetailUnknown = 'TaskKindDetailUnknown',
}

export interface V1TaskDeviceInfo {
  deviceIp?: string
  deviceName?: string
  deviceType?: string
  isOnline?: boolean
}

export interface TaskVerificationInfo {
  /**
   * dnat
   */
  dNat?: string
  /**
   * 攻击目标
   */
  dstAgentName?: string
  dstBizAreaName?: string
  /**
   * 中间经过 IP
   */
  middleIps?: string[]
  /**
   * snat
   */
  sNat?: string
  /**
   * 攻击源
   */
  srcAgentName?: string
  srcBizAreaName?: string
  [property: string]: any
}

export interface DetailPropsType {
  areaName?: string
  createTime?: string
  taskTime?: string
  cycleDays?: string
  cycleTime?: string
  db?: string
  encode?: Baswafv1Encode
  enctypeTypes?: string[]
  groupName?: string
  language?: string
  os?: string
  poolName?: string
  priority?: number
  reduceSpeed?: number
  site?: string
  taskId?: string
  taskKind?: Baswafv1TaskKindDetail
  taskName?: string
  templateNames?: string[]
  verificationInfo?: TaskVerificationInfo
  wafName?: string
  reportInfos?: {
    name: string
    email: string
  }[]
}

interface DetailDisplayType extends Omit<DetailPropsType, 'taskKind'> {
  taskKind?: string
  devices?: V1TaskDeviceInfo[]
}

interface TemplateInfo {
  name: string
  resourceAmount: number
  updateTime: string
  templateId: string
}

interface TemplateResponse {
  list: TemplateInfo[]
  total: number
}

enum SortOrder {
  SORT_ORDER_UNSPECIFIED = 'SORT_ORDER_UNSPECIFIED',
  SORT_ORDER_ASCENDING = 'SORT_ORDER_ASCENDING',
  SORT_ORDER_DESCENDING = 'SORT_ORDER_DESCENDING',
}

const KindMap = {
  TaskKindDetailImmediately: t('立即任务'),
  TaskKindDetailBooking: t('预定任务'),
  TaskKindDetailCycleDay: t('周期任务 每日'),
  TaskKindDetailCycleWeek: t('周期任务 每周'),
  TaskKindDetailCycleMonth: t('周期任务 每月'),
}

const EncodeMap = {
  EncodeDynamic: t('动态'),
  EncodeStatic: t('静态'),
  EncodeUnknown: t('未知'),
}

const WeekMap = {
  1: t('周一'),
  2: t('周二'),
  3: t('周三'),
  4: t('周四'),
  5: t('周五'),
  6: t('周六'),
  0: t('周日'),
}

// 提取验证模式组件
const VerificationMode: React.FC<{ verificationInfo?: TaskVerificationInfo }> = ({
  verificationInfo,
}) => (
  <div className="verification-item">
    <div className="verification-label">{t('验证模式：')}</div>
    <div className="verification-content">
      <div className="config-name">
        <span className="config-label">{t('模式名称：')}</span>
        {verificationInfo?.srcAgentName && verificationInfo?.dstAgentName ? (
          <div className="config-value">
            <span>agent</span>
            <span className="arrow">→</span>
            <span>agent</span>
          </div>
        ) : (
          '-'
        )}
      </div>
      <div className="asset-info">
        <div className="asset-item">
          <span className="asset-label">{t('Agent名称：')}</span>
          <span className="asset-value">{verificationInfo?.srcAgentName || '-'}</span>
        </div>
        <div className="asset-item">
          <span className="asset-label">{t('Agent名称：')}</span>
          <span className="asset-value">{verificationInfo?.dstAgentName || '-'}</span>
        </div>
      </div>
      <div className="asset-info">
        <div className="asset-item">
          <span className="asset-label">{t('安全域：')}</span>
          <span className="asset-value">{verificationInfo?.srcBizAreaName || '-'}</span>
        </div>
        <div className="asset-item">
          <span className="asset-label">{t('安全域：')}</span>
          <span className="asset-value">{verificationInfo?.dstBizAreaName || '-'}</span>
        </div>
      </div>
      <div className="middle-ips">
        <span className="middle-ips-label">{t('过程IP：')}</span>
        <span className="middle-ips-value">{verificationInfo?.middleIps?.join(',') || '-'}</span>
      </div>
    </div>
  </div>
)

// 提取执行类型组件
const ExecutionType: React.FC<{ taskDetail?: DetailDisplayType; isWeeklyTask: boolean }> = ({
  taskDetail,
  isWeeklyTask,
}) => {
  const renderTaskKind = () => {
    const { taskKind, taskTime, cycleDays } = taskDetail || {}

    if (taskKind === t('立即任务')) return `立即 ${taskTime}`
    if (taskKind === t('预定任务')) return `预定任务 ${taskTime}`
    if (taskKind === t('周期任务 每日')) return `周期任务 每日 ${taskTime}`

    return (
      <>
        <span>{taskKind}</span>
        <span style={{ marginLeft: '10px' }}>
          {cycleDays?.split(',')?.map(day => (
            <span
              style={{
                marginRight: '5px',
                marginBottom: '5px',
                display: 'inline-block',
              }}
              key={day}
            >
              {isWeeklyTask ? WeekMap[day] : `${day}号`}{' '}
            </span>
          ))}
        </span>
        <span style={{ marginLeft: '5px' }}>{taskTime}</span>
      </>
    )
  }

  return (
    <div className="item">
      <div className="label">{t('执行类型：')}</div>
      <div className="value">{renderTaskKind()}</div>
    </div>
  )
}

// 提取设备信息组件
const DeviceInfo: React.FC<{ devices?: V1TaskDeviceInfo[] }> = ({ devices }) => {
  if (!devices?.length) return null

  return (
    <div className="device-info">
      <div className="title">
        <span>{t('关联安全设备')}</span>
      </div>
      <div className="device-list">
        {devices.map((device, index) => (
          <div key={index} className="device-item">
            <div className="device-field">
              <span className="field-label">{t('设备类型：')}</span>
              <span className="field-value">{device.deviceType || 'WAF'}</span>
            </div>
            <div className="device-field">
              <span className="field-label">{t('设备名称：')}</span>
              <span className="field-value">{device.deviceName || t('设备名称')}</span>
            </div>
            <div className="device-field">
              <span className="field-label">{t('设备IP：')}</span>
              <span className="field-value">{device.deviceIp}</span>
            </div>
            <div className="device-field">
              <span className="status-dot" data-status={device.isOnline ? 'online' : 'offline'} />
              <span className="status-text">{device.isOnline ? t('在线') : t('离线')}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// 提取报表发送人组件
const ReportRecipients: React.FC<{ reportInfos?: { name: string; email: string }[] }> = ({
  reportInfos,
}) => {
  if (!reportInfos?.length) return null

  return (
    <div className="item">
      <div className="label">{t('报表发送人：')}</div>
      <div className="value">
        {reportInfos.map((report, index) => (
          <Tooltip
            key={index}
            title={report.email || t('暂无邮箱信息')}
            getPopupContainer={() => document.body}
          >
            <span className="encode-tag">{report.name}</span>
          </Tooltip>
        ))}
      </div>
    </div>
  )
}

// 简化排序逻辑
const getSortOrder = (
  sorter: SorterResult<TemplateInfo> | SorterResult<TemplateInfo>[],
): SortOrder => {
  const sorterObj = Array.isArray(sorter) ? sorter[0] : sorter
  if (sorterObj?.order === 'ascend') return SortOrder.SORT_ORDER_ASCENDING
  if (sorterObj?.order === 'descend') return SortOrder.SORT_ORDER_DESCENDING
  return SortOrder.SORT_ORDER_UNSPECIFIED
}

const DetailDrawer: React.FC<PropsType> = ({ open, onClose, id }) => {
  const [getTaskDetail] = useGetTaskDetail()
  const [getTaskDetailTemplateList, loading] = useGetTaskDetailTemplateList()
  const [taskDetail, setTaskDetail] = useState<DetailDisplayType>()
  const [templateData, setTemplateData] = useState<TemplateResponse>({
    list: [],
    total: 0,
  })
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 10,
    sortOrder: SortOrder.SORT_ORDER_UNSPECIFIED,
    taskId: id,
  })
  const [caseModalVisible, setCaseModalVisible] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')

  const buildDetailData = (data: DetailPropsType): DetailDisplayType => {
    if (!data.taskKind) return data
    return {
      ...data,
      taskKind: KindMap[data.taskKind],
    }
  }

  // 提取初始化搜索参数
  const getInitialSearchParams = () => ({
    page: 1,
    pageSize: 10,
    sortOrder: SortOrder.SORT_ORDER_UNSPECIFIED,
    taskId: '',
  })

  useEffect(() => {
    if (!open) {
      setSearchParams(getInitialSearchParams())
      return
    }
    getTaskDetail(id)
      .then(res => {
        setTaskDetail(buildDetailData(res.data))
      })
      .catch(_ => {})
  }, [open])

  useEffect(() => {
    if (!open || !id) return
    getTaskDetailTemplateList({ ...searchParams, taskId: id })
      .then(res => {
        setTemplateData({
          list: res.data.list || [],
          total: res.data.total || 0,
        })
      })
      .catch(_ => {})
  }, [open, searchParams, id])

  const isWeeklyTask = Boolean(taskDetail?.taskKind?.includes(t('每周')))

  // 提取表格列配置
  const getTemplateColumns = (): ColumnType<TemplateInfo>[] => [
    {
      title: t('模板名称'),
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      ellipsis: true,
    },
    {
      title: t('用例数量'),
      dataIndex: 'resourceAmount',
      key: 'resourceAmount',
      width: '30%',
      render: (count: number, record: TemplateInfo) => (
        <button
          type="button"
          className="usage-count-btn"
          onClick={() => {
            setSelectedTemplate(record.templateId)
            setCaseModalVisible(true)
          }}
        >
          {count}
        </button>
      ),
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: '35%',
      sorter: true,
      sortOrder:
        searchParams.sortOrder === SortOrder.SORT_ORDER_UNSPECIFIED
          ? undefined
          : searchParams.sortOrder === SortOrder.SORT_ORDER_ASCENDING
          ? 'ascend'
          : 'descend',
      render: (time: string) => (
        <span>{time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
      ),
    },
  ]

  const templateColumns = getTemplateColumns()

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<TemplateInfo> | SorterResult<TemplateInfo>[],
  ) => {
    setSearchParams(prev => ({
      ...prev,
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortOrder: getSortOrder(sorter),
    }))
  }

  return (
    <>
      <DrawerWrapper
        title={<SignTitle show>{t('任务详情')}</SignTitle>}
        width={720}
        open={open}
        onClose={onClose}
        footer={null}
        zIndex={1000}
      >
        <div>
          <div className="task-config">
            <div className="title">
              <span>{t('任务配置信息')}</span>
            </div>
            <div className="content">
              <div className="item">
                <span className="label">{t('任务名称：')}</span>
                <Typography.Text ellipsis={{ tooltip: true }} style={{ width: '100%' }}>
                  {taskDetail?.taskName}
                </Typography.Text>
              </div>
              <div className="item">
                <div className="label">{t('任务分组：')}</div>
                <div className="value">{taskDetail?.groupName || '-'}</div>
              </div>
              <VerificationMode verificationInfo={taskDetail?.verificationInfo} />
              <ExecutionType taskDetail={taskDetail} isWeeklyTask={isWeeklyTask || false} />
              <div className="item">
                <div className="label">{t('任务优先级：')}</div>
                <div className="value">{taskDetail?.priority || '3'}</div>
              </div>
              <div className="item">
                <div className="label">{t('创建时间：')}</div>
                <div className="value">{taskDetail?.createTime}</div>
              </div>
              <div className="item">
                <div className="label">{t('报表发送：')}</div>
                <div className="value">
                  {taskDetail?.reportInfos && taskDetail.reportInfos.length > 0 ? t('开') : t('关')}
                </div>
              </div>
              <ReportRecipients reportInfos={taskDetail?.reportInfos} />
            </div>
          </div>

          <DeviceInfo devices={taskDetail?.devices} />

          <div className="template-info">
            <div className="title">
              <span>{t('模板信息')}</span>
            </div>
            <Table
              className="template-table"
              columns={templateColumns}
              dataSource={templateData.list}
              showSorterTooltip={false}
              loading={loading}
              pagination={{
                current: searchParams.page,
                total: templateData.total,
                pageSize: searchParams.pageSize,
                showSizeChanger: true,
                size: 'small',
                showTotal: total => `共 ${total} 条`,
              }}
              onChange={handleTableChange}
              size="small"
              rowKey="templateId"
            />
          </div>
        </div>
        <CaseModal
          open={caseModalVisible}
          onClose={() => {
            setCaseModalVisible(false)
          }}
          taskId={id}
          templateId={selectedTemplate}
        />
      </DrawerWrapper>
    </>
  )
}

export default DetailDrawer

const DrawerWrapper = styled(Drawer)`
  .ant-drawer-close {
    width: 36px;
    height: 36px;
    background: #d8d6f4;
    box-shadow: -1px 4px 10px 0px #c7c7c7;
    border-radius: 6px;
    border: none;
    margin-right: 0px;
    color: #4238c9;
    font-size: 16px;
    font-weight: 500;
    position: absolute;
    right: 0px;
    top: 0px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translate(-10px, 8px);
      box-shadow: 0 0 0 0;
    }

    .ant-drawer-close-x {
      width: 100%;
      height: 100%;
      line-height: 36px;
      text-align: center;
    }
  }

  .ant-drawer-header {
    height: 56px;
    background: #f2f0ff;
    border-bottom: none;
    margin: 0;
  }

  .task-config,
  .device-info,
  .template-info {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;

    .title {
      font-size: 14px;
      font-weight: 500;
      color: #1d2129;
      margin-bottom: 16px;
      position: relative;
      /* padding-left: 10px; */

      /* &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 2px;
        height: 14px;
        background: #165dff;
      } */
    }

    .content {
      display: flex;
      flex-direction: column;

      .item {
        margin-bottom: 16px;
        display: flex;
        align-items: flex-start;

        .label {
          min-width: 80px;
          color: #86909c;
          margin-right: 8px;
          line-height: 20px;
          flex-shrink: 0;
        }

        .value {
          color: #1d2129;
          line-height: 20px;
          flex: 1;
        }
      }
    }
  }

  .ant-table-column-sorters {
    justify-content: flex-start;
    .ant-table-column-sorter {
      padding-top: 2px;
      margin-left: 5px;
    }
    .ant-table-column-title {
      flex: none;
    }
  }

  .device-list {
    .device-item {
      padding: 12px 16px;
      background: #f7f8fa;
      border-radius: 2px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:last-child {
        margin-bottom: 0;
      }

      .device-field {
        display: flex;
        align-items: center;
        // margin-bottom: 8px;
        color: #4e5969;
        font-size: 13px;
        line-height: 20px;

        // &:last-child {
        //   margin-bottom: 0;
        // }

        .field-label {
          color: #86909c;
          margin-right: 8px;
          min-width: 70px;
        }

        .field-value {
          color: #4e5969;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-right: 4px;

          &[data-status='online'] {
            background: #00b42a;
          }

          &[data-status='offline'] {
            background: #86909c;
          }
        }

        .status-text {
          color: #4e5969;
        }
      }
    }
  }

  .template-table {
    .ant-table {
      background: #f7f8fa;
      border-radius: 2px;
    }

    .ant-table-thead > tr > th {
      background: #f2f3f5;
      color: #1d2129;
      font-weight: 500;
      padding: 12px 16px;
      border-bottom: 1px solid #e5e6eb;
    }

    .ant-table-tbody > tr > td {
      background: #f7f8fa;
      border-bottom: 1px solid #e5e6eb;
      padding: 12px 16px;
    }

    .template-name {
      color: #1d2129;
    }

    .usage-count-btn {
      color: #4238c9;
      border: none;
      background: none;
      padding: 0;
      cursor: pointer;
      font-size: inherit;
    }

    .update-time {
      color: #86909c;
    }

    .ant-pagination {
      margin: 16px 0 0;
      text-align: right;

      .ant-pagination-total-text {
        color: #86909c;
      }
    }
  }

  .encode-tag {
    display: inline-block;
    padding: 1px 8px;
    background: #f2f3f5;
    border-radius: 2px;
    font-size: 12px;
    color: #4e5969;
    margin-right: 8px;
    margin-bottom: 8px;
    line-height: 20px;

    &:last-child {
      margin-right: 0;
    }
  }

  .verification-item {
    margin-bottom: 16px;

    .verification-label {
      color: #86909c;
      font-size: 13px;
      line-height: 20px;
      margin-bottom: 8px;
    }

    .verification-content {
      background: #f7f8fa;
      border-radius: 2px;
      padding: 12px;
      .asset-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .asset-item {
          width: 50%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          .asset-label {
            color: #86909c;
            margin-right: 8px;
          }
        }
      }
      .middle-ips {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        .middle-ips-label {
          color: #86909c;
          margin-right: 8px;
          min-width: 70px;
          flex-shrink: 0;
        }
        .middle-ips-value {
          color: #4e5969;
          flex: 1;
        }
      }

      .config-name {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        font-size: 13px;
        line-height: 20px;

        .config-label {
          color: #86909c;
          margin-right: 8px;
          min-width: 70px;
          flex-shrink: 0;
        }

        .config-value {
          display: flex;
          align-items: center;
          color: #1d2129;
          flex: 1;

          .arrow {
            color: #4238c9;
            margin: 0 8px;
            font-family: SimSun, sans-serif;
          }
        }
      }

      .asset-info {
        .asset-item {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          font-size: 13px;
          line-height: 20px;

          &:last-child {
            margin-bottom: 0;
          }

          .asset-label {
            color: #86909c;
            margin-right: 8px;
            min-width: 70px;
            flex-shrink: 0;
          }

          .asset-value {
            color: #4e5969;
            flex: 1;
          }
        }
      }
    }
  }
`
